/**
 * Advisor payout approval (L15, L16) display helpers. `pending` reads "Awaiting
 * approval"; a rejected payout is `canceled` + `rejectedAt`; an advisor-cancelled
 * one is `canceled` + `canceledByUserId`.
 */

/** Payout status key → founder-facing label (L16: pending reads "Awaiting approval"). */
export const advisorPayoutStatusLabel = (payout: {
  statusKey?: string | null
  rejectedAt?: unknown
  canceledByUserId?: string | null
  status?: { title?: string | null } | null
}): string => {
  switch (payout.statusKey) {
    case 'pending':
      return 'Awaiting approval'
    case 'processing':
      return 'Sending'
    case 'paid':
      return 'Paid'
    case 'failed':
      return 'Failed'
    case 'canceled':
      if (payout.rejectedAt) {
        return 'Rejected'
      }
      return 'Canceled'
    default:
      return payout.status?.title || payout.statusKey || '—'
  }
}

export const advisorPayoutStatusPalette = (payout: {
  statusKey?: string | null
  rejectedAt?: unknown
}): string => {
  switch (payout.statusKey) {
    case 'pending':
    case 'processing':
      return 'yellow'
    case 'paid':
      return 'green'
    case 'failed':
      return 'red'
    case 'canceled':
      return payout.rejectedAt ? 'red' : 'gray'
    default:
      return 'gray'
  }
}

/** One open payout per advisor (L16): pending or processing blocks a new request. */
export const hasOpenAdvisorPayout = (payouts: Array<{ statusKey?: string | null } | null | undefined>) =>
  payouts.some(p => p?.statusKey === 'pending' || p?.statusKey === 'processing')

const PAYOUT_ERROR_COPY: Record<string, string> = {
  ADVISOR_PAYOUT_ALREADY_PENDING:
    'You already have a payout awaiting approval. Cancel it or wait for it to be reviewed before requesting another.',
  ADVISOR_PAYOUT_NOT_PENDING:
    'This payout is no longer awaiting approval. Refresh to see its current status.',
  ADVISOR_PAYOUT_SELF_REVIEW:
    'You cannot approve or reject your own payout. Another Beancounter admin must review it.',
  ADVISOR_PAYOUT_REJECTION_REASON_REQUIRED:
    'Add a reason — the advisor sees it in their email and payout history.',
  ADVISOR_PAYOUT_NOT_APPROVED: 'This payout has not been approved, so no transfer was sent.',
  ADVISOR_PAYOUT_BELOW_MINIMUM: 'That amount is below the payout minimum.',
  ADVISOR_PAYOUT_CONNECT_REQUIRED: 'Connect a Stripe account before requesting a payout.',
  USER_NOT_AUTHORIZED: 'You do not have permission to do that.',
}

/** Maps payout GraphQL error keys to readable copy; unknown errors keep their message. */
export const advisorPayoutErrorMessage = (error: unknown, fallback: string): string => {
  const record = error as { errors?: Array<{ message?: string }>; message?: string } | null
  const raw = (typeof error === 'string' ? error : record?.errors?.[0]?.message || record?.message || '')
    .replace(/^(ApolloError|CombinedGraphQLErrors|GraphQL error|Error):\s*/i, '')
    .trim()
  const key = Object.keys(PAYOUT_ERROR_COPY).find(code => raw.includes(code))
  return key ? PAYOUT_ERROR_COPY[key] : raw || fallback
}

/** Who/when line for a reviewed payout, e.g. "Rejected by Ada Admin". */
export const advisorPayoutReviewer = (payout: {
  statusKey?: string | null
  rejectedAt?: unknown
  approvedAt?: unknown
  canceledByUserId?: string | null
  userId?: string | null
  approvedByUser?: { fullName?: string | null; displayName?: string | null } | null
  rejectedByUser?: { fullName?: string | null; displayName?: string | null } | null
}): string | null => {
  const name = (u?: { fullName?: string | null; displayName?: string | null } | null) =>
    u?.displayName || u?.fullName || null
  if (payout.rejectedAt) {
    return name(payout.rejectedByUser) ? `Rejected by ${name(payout.rejectedByUser)}` : 'Rejected'
  }
  if (payout.statusKey === 'canceled' && payout.canceledByUserId) {
    return payout.canceledByUserId === payout.userId ? 'Canceled by the advisor' : 'Canceled'
  }
  if (payout.approvedAt) {
    return name(payout.approvedByUser) ? `Approved by ${name(payout.approvedByUser)}` : 'Approved'
  }
  return null
}
